import React from "react";
import {
  Flex,
  Text,
  Box,
  Grid,
  Badge,
  Divider,
  Link,
  HStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

interface RiskItem {
  badges: string[];
  title: string;
  risk: string;
  riskColor: string;
  riskBg: string;
}

const RiskCard: React.FC<RiskItem> = ({
  badges,
  title,
  risk,
  riskColor,
  riskBg,
}) => {
  const { t } = useTranslation("common");

  return (
    <Box
      border="1px solid #E0E0E0"
      borderRadius="16px"
      p="12px"
      display="flex"
      flexDirection="column"
      gap="12px"
      bg="white"
      boxShadow="2px 2px 10px 0px rgba(0, 0, 0, 0.1)"
    >
      {/* Top badges */}
      <HStack spacing="8px">
        {badges.map((b, i) => (
          <Badge
            key={i}
            bg="#00B8960F"
            color="#00B896"
            variant="subtle"
            p="2px 12px"
            borderRadius="16px"
            fontSize="14px"
            fontWeight="500"
          >
            {b}
          </Badge>
        ))}
      </HStack>

      {/* Title */}
      <Text fontSize="lg" fontWeight="bold" color="black">
        {title}
      </Text>

      {/* Risk level */}
      <HStack spacing="8px">
        <Text fontSize="14px" fontWeight="500" color="#5C6670">
          {t("hazardsCards.riskLevel")}
        </Text>
        <Badge
          p="2px 12px"
          borderRadius="16px"
          bg={riskBg}
          color={riskColor}
          fontSize="14px"
          fontWeight="500"
        >
          {risk}
        </Badge>
      </HStack>

      <Divider />

      <Flex justify="flex-end" align="center">
        <Link fontSize="14px" color="#005EB8" fontWeight="500">
          {t("recentRiskCard.seeDetails")} <ArrowForwardIcon ml={1} />
        </Link>
      </Flex>
    </Box>
  );
};

const RecentRiskCard: React.FC = () => {
  const { t } = useTranslation("common");

  const risks: RiskItem[] = [
    {
      badges: [t("recentRiskCard.system"), t("recentRiskCard.internal")],
      title: t("recentRiskCard.dataBreach"),
      risk: t("recentRiskCard.high"),
      riskColor: "#DE7F04",
      riskBg: "#DE7F040A",
    },
    {
      badges: [t("recentRiskCard.process"), t("recentRiskCard.audit")],
      title: t("recentRiskCard.complianceGap"),
      risk: t("recentRiskCard.medium"),
      riskColor: "#3AB65A",
      riskBg: "#3AB65A0A",
    },
    {
      badges: [t("recentRiskCard.people"), t("recentRiskCard.hr")],
      title: t("recentRiskCard.staffShortage"),
      risk: t("recentRiskCard.low"),
      riskColor: "#DC3545",
      riskBg: "#DC35450A",
    },
    {
      badges: [t("recentRiskCard.vendor"), t("recentRiskCard.external")],
      title: t("recentRiskCard.supplierDelay"),
      risk: t("recentRiskCard.high"),
      riskColor: "#DE7F04",
      riskBg: "#DE7F040A",
    },
    {
      badges: [t("recentRiskCard.tech"), t("recentRiskCard.cloud")],
      title: t("recentRiskCard.outageRisk"),
      risk: t("recentRiskCard.medium"),
      riskColor: "#3AB65A",
      riskBg: "#3AB65A0A",
    },
    {
      badges: [t("recentRiskCard.finance"), t("recentRiskCard.budget")],
      title: t("recentRiskCard.overspend"),
      risk: t("recentRiskCard.low"),
      riskColor: "#DC3545",
      riskBg: "#DC35450A",
    },
  ];

  return (
    <Flex
      minW="600px"
      border="1px solid #E0E0E0"
      borderRadius="16px"
      p="24px"
      flexDirection="column"
      gap={4}
      bg="white"
    >
      <Text fontSize="24px" fontWeight="bold" color="black">
        {t("recentRiskCard.title")}
      </Text>
      <Text fontSize="18px" fontWeight="500" color="#5C6670">
        {t("recentRiskCard.description")}
      </Text>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        {risks?.map((r, idx) => (
          <RiskCard key={idx} {...r} />
        ))}
      </Grid>
    </Flex>
  );
};

export default RecentRiskCard;
